import { useEffect, useState, useRef, useCallback } from 'react';

const useInfiniteScroll = (load, options, shouldExecute) => {
  const [isLoading, setIsLoading] = useState(shouldExecute);
  const [isFetching, setIsFetching] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [data, setData] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loadingNextPage, setLoadingNextPage] = useState(false);
  const [error, setError] = useState(false);

  const abortControllerRef = useRef(null);

  const useVisibility = (cb, deps) => {
    const intersectionObserver = useRef(null);
    return useCallback((node) => {
      if (intersectionObserver.current) {
        intersectionObserver.current.disconnect();
      }
      intersectionObserver.current = new IntersectionObserver(([entry]) => {
        cb(entry.isIntersecting && hasMore);
      });
      if (node) intersectionObserver.current.observe(node);
    }, deps);
  };

  // eslint-disable-next-line no-shadow
  const loadAsync = async (options, callback) => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    const { signal } = abortControllerRef.current;

    setIsFetching(true);
    try {
      const response = await load({ ...options, page: options.page || page }, signal);
      if (response.pagination && response.pagination.current_page) {
        setHasMore(response.pagination.current_page < response.pagination.total_pages);
        if (response.pagination.current_page < response.pagination.total_pages)
          setPage(response.pagination.current_page + 1);
      }
      setData((prevState) =>
        refresh ? response[options.slug] : [...prevState, ...response[options.slug]]
      );
      setIsFetching(false);
      setIsLoading(false);
      setRefresh(false);
      if (loadingNextPage) setLoadingNextPage(false);
      if (callback) callback();
    } catch (err) {
      if (!err.includes('signal is aborted')) {
        setData([]);
        setError(error);
        setIsFetching(false);
        setIsLoading(false);
        setRefresh(false);
        if (loadingNextPage) setLoadingNextPage(false);
        if (callback) callback();
      }
    }
  };

  const lastItem = useVisibility(
    (visible) => {
      let didCancel = false;

      if (visible) {
        setLoadingNextPage(true);
        loadAsync(options).then(() => {
          if (!didCancel) {
            setLoadingNextPage(false);
          }
        });
      }

      return () => {
        didCancel = true;
        setLoadingNextPage(false);
      };
    },
    [page, data]
  );

  useEffect(() => {
    let didCancel = false;

    if (refresh && !didCancel) {
      setPage(1);
      setIsLoading(true);
      setRefresh(false);
      loadAsync({ ...options, page: 1 }, () => {
        setLoaded(true);
      });
    }
    return () => {
      didCancel = true;
    };
  }, [refresh]);

  return {
    data,
    setData,
    refresh,
    setRefresh,
    lastItem,
    hasMore,
    isLoading,
    isFetching,
    setIsFetching,
    setIsLoading,
    loaded,
    setLoaded,
    error,
    loadingNextPage,
    abortControllerRef,
  };
};

export default useInfiniteScroll;
